import React, { useState, useEffect } from 'react';
import './ListViewWithModal.css';
import StbButton from '../security-toolbox-ui/StbButton';
import StbModal from '../security-toolbox-ui/StbModal';
import { HbrOption, HbrSelect } from '@harbor/elements-react-standalone';


interface ListViewWithModalProps<T> {
  title: string;
  items: T[];
  availableItems: T[];
  onAddItem: (item: T) => void;
  onDeleteItem: (id: string) => void;
  fetchAvailableItems?: () => Promise<void>;
  idKey: keyof T;
  nameKey: keyof T;
  descriptionKey: keyof T;
}

function ListViewWithModal<T extends { [key: string]: any }>({
  title,
  items,
  availableItems,
  onAddItem,
  onDeleteItem,
  fetchAvailableItems,
  idKey,
  nameKey,
  descriptionKey,
}: ListViewWithModalProps<T>) {
  const [openAddModal, setOpenAddModal] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [isFetched, setIsFetched] = useState(false);
  const [selectedItemDetails, setSelectedItemDetails] = useState<{ name: string; description: string } | null>(null);
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);

  useEffect(() => {
    if (!isFetched && availableItems.length === 0 && fetchAvailableItems) {
      fetchAvailableItems().then(() => setIsFetched(true));
    }
  }, [availableItems, fetchAvailableItems, isFetched]);

  const handleAddClick = () => {
    setOpenAddModal(true);
  };

  const handleModalClose = async () => {
    setOpenAddModal(false);
    setSelectedItemId(null);
    setSelectedItemDetails(null);
  };

  const handleAddNewItem = async () => {
    const selectedItem = availableItems.find(item => String(item[idKey]) === selectedItemId);
    if (selectedItem) {
      onAddItem(selectedItem);
    }
    handleModalClose();
  };

  const handleItemClick = (item: T) => {
    setSelectedItemDetails({
      name: String(item[nameKey]),
      description: String(item[descriptionKey]),
    });
  };

  return (
      <>
        <div className="flex justify-between items-center">
          <h3>{title}</h3>
          <StbButton role="add" onClick={handleAddClick} size="xsmall" />
        </div>
        <ul className="w-full p-0 m-0 mb-4">
          {items.map(item => (
            <li
              key={String(item[idKey])}
              className="text-sm flex list-none items-center min-h-7 cursor-pointer hover:text-black"
              onClick={() => handleItemClick(item)}
              onMouseEnter={() => setHoveredItemId(String(item[idKey]))}
              onMouseLeave={() => setHoveredItemId(null)}
            >
              <div className="flex-1 truncate">
                {String(item[nameKey])}
              </div>
              <StbButton
                role="delete"
                iconOnly
                variant="text"
                size="xsmall"
                className={`flex-0 ${hoveredItemId === String(item[idKey]) ? ' visible' : ' hidden'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteItemId(String(item[idKey]));
                  setConfirmDelete(true);
                }}
              />
            </li>
          ))}
        </ul>

        {/* Details Modal */}
        <StbModal
          title={selectedItemDetails?.name}
          role="information"
          open={!!selectedItemDetails}
          onClose={handleModalClose}
        >
          <div>
            {selectedItemDetails 
              ? selectedItemDetails.description
              : "No details available."}
          </div>
        </StbModal>

        {/* Add Modal */}
        <StbModal
          title="Add New Item"
          role="save"
          onClose={handleModalClose}
          onSubmit={handleAddNewItem}
          submitLabel="Add Item"
          submitDisabled={selectedItemId === null}
          open={openAddModal}
        >
          <HbrSelect
            value={selectedItemId || ''}
            onHbr-change={(e) => setSelectedItemId(e.target.value as string)}
            placeholder="Select an item"
          >
            {availableItems.map(item => (
              <HbrOption key={String(item[idKey])} value={String(item[idKey])}>
                  {String(item[nameKey])}
              </HbrOption>
            ))}
          </HbrSelect>
        </StbModal>

        {/* Delete Modal */}
        <StbModal
          title="Confirm Delete"
          role="delete"
          onClose={async () => setConfirmDelete(false)}
          onSubmit={async () => {
            if (deleteItemId) {
              onDeleteItem(deleteItemId);
            }
          }}
          submitLabel="Remove Item"
          open={confirmDelete}

        >
          <div>Are you sure you want to delete this item?</div>
        </StbModal>
      </>
  );
}

export default ListViewWithModal;